import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { ReportSchemaType } from 'modules/ai_reports/lib/types'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

type TableVisualization = Extract<
  React.ComponentProps<typeof OneDataCollection>['visualizations'][number],
  { type: 'table' }
>

export type ColumnFormatHint = {
  column_alias: string
  column_format: string
}

const pickRenderer = (
  dataType: string,
  formatHint: string | undefined
): TableVisualization['options']['columns'][number]['render'] => {
  switch (formatHint) {
    case 'monetary_amount':
      return (cell) => {
        return {
          type: 'text',
          value: formattedAmount({
            amountCents: Number(cell.value) * 100,
            currency: String(cell.currency),
            locale: String(cell.locale),
          }),
        }
      }
    case 'date':
      return (cell) => ({
        type: 'date',
        value: cell.value && new Date(String(cell.value)),
      })
    case 'array':
      return (cell) => ({
        type: 'tagList',
        value: {
          type: 'raw',
          tags: String(cell.value)
            .split(',')
            .map((tag) => ({ text: tag.trim() }))
            .filter((tag) => tag.text !== ''),
        },
      })
  }

  switch (dataType) {
    case 'number':
      return (cell) => ({
        type: 'number',
        value: cell.value,
      })
    case 'boolean':
      return (cell) => ({
        type: 'dotTag',
        value: {
          label:
            cell.value === 'true'
              ? i18n.t('ai_reports.boolean.true')
              : i18n.t('ai_reports.boolean.false'),
          color: cell.value === 'true' ? 'viridian' : 'radical',
        },
      })
    case 'array':
      return (cell) => ({
        type: 'tagList',
        value: {
          type: 'raw',
          tags: JSON.parse(String(cell.value))
            .map((tag: string) => tag.trim())
            .filter((tag: string) => tag !== '')
            .map((text: string) => ({ text })),
        },
      })
    default:
      return (cell) => ({
        type: 'text',
        value: cell.value,
      })
  }
}

export const formatColumns = (
  schemas: ReportSchemaType,
  columnFormatHints: ColumnFormatHint[],
  locale: string
): TableVisualization['options']['columns'] => {
  // FIXME: This two things need to be merged beforehand, this is too late.
  const currencyColumn =
    schemas.find((schema) => schema.formatHint === 'monetary_currency')?.name ??
    columnFormatHints.find((hint) => hint.column_format === 'monetary_currency')?.column_alias

  return (
    schemas.map((schema) => {
      const columnType = schema.dataType
      const columnFormatHint =
        schema.formatHint ??
        columnFormatHints.find((hint) => hint.column_alias === schema.name)?.column_format
      const renderer = pickRenderer(columnType, columnFormatHint)

      const label = schema.displayName ?? schema.name ?? ''
      const align = columnFormatHint === 'monetary_amount' ? 'right' : 'left'

      return {
        label,
        align,
        render: (row) => {
          const value = row[schema.name]
          const currency = currencyColumn && row[currencyColumn]

          return renderer({ value, currency, locale })
        },
      }
    }) ?? []
  )
}

function parseDatasourceType(name: string) {
  switch (name) {
    case 'snapshot':
    case 'traceability':
      return name
    default:
      throw new Error(`Unknown datasource type ${name}`)
  }
}

const PATTERN_DATASOURCE_TYPE = /.*_(current|snapshot|traceability)$/
export function datasourceTypeFromId(id: string) {
  const match = PATTERN_DATASOURCE_TYPE.exec(id)
  if (!match) throw new Error(`Cannot parse type from datasource ID ${id}`)
  const type = match[1]
  if (!type)
    throw new Error(`Error parsing type from datasource ID ${id} - ${JSON.stringify(match)}`)

  return parseDatasourceType(type)
}
